// src/pages/safety/Checklist.jsx
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  listSafetyChecklists,
  saveSafetyDoc,
} from "../../api/safety";
import { softDeleteDoc } from "../../api/papers";
import MobileSidePanel from "../../components/common/mobile/MobileSidePanel";
import ReusableCalendar, {
  buildCounts,
} from "../../components/common/feature/Calendar";
import ChecklistView from "../../components/safety/view/ChecklistView";
import ChecklistEditor from "../../components/safety/editor/ChecklistEditor";
import MobileChecklistEditor from "../../components/safety/editor/MobileChecklistEditor";
import MobileSafetySignalLight from "../../components/widgets/MobileSafetySignalLight";

// ─────────────────────────────────────────────
// 스타일 유틸 (색상/배지/이벤트 카드)
// ─────────────────────────────────────────────
const dotHex = (c) =>
  c === "blue"
    ? "#2563eb"
    : c === "green"
      ? "#16a34a"
      : c === "yellow"
        ? "#f59e0b"
        : c === "red"
          ? "#ef4444"
          : "#9ca3af";

const StatusPill = ({ label, color }) => {
  const base =
    "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1";
  const theme = {
    blue: "bg-blue-50 text-blue-700 ring-blue-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    yellow: "bg-amber-50 text-amber-700 ring-amber-200",
    red: "bg-rose-50 text-rose-700 ring-rose-200",
    gray: "bg-gray-50 text-gray-600 ring-gray-200",
  }[color || "gray"];
  return (
    <span className={`${base} ${theme}`}>
      <span className="mr-1.5" style={{ color: dotHex(color) }}>
        ●
      </span>
      {label}
    </span>
  );
};


const fmtDay = (d) => format(d, "yyyy-MM-dd");
const dayFromStr = (s) => {
  if (!s) return "";
  if (typeof s === "string") return s.slice(0, 10);
  if (s instanceof Date) return format(s, "yyyy-MM-dd");
  return "";
};

// 상태 정규화: 띄어쓰기/동의어 보정
const normalizeStatus = (raw) => {
  const v = String(raw || "").trim();
  if (v === "진행중") return "진행 중";
  if (v === "조치필요") return "조치 필요";
  return v;
};

const progressToColor = (p) => {
  switch (p) {
    case "completed":
      return "green";
    case "planned":
    case "in_progress":
    case "action_needed":
      return "yellow";
    case "failed":
    case "urgent":
      return "red";
    case "cancelled":
      return "gray";
    default:
      return "gray";
  }
};

const progressLabel = (p) => {
  switch (p) {
    case "completed":
      return "적합(완료)";
    case "planned":
      return "예정";
    case "in_progress":
      return "진행 중";
    case "action_needed":
      return "조치 필요";
    case "failed":
      return "부적합";
    case "urgent":
      return "긴급";
    case "cancelled":
      return "취소";
    default:
      return "미정";
  }
};

// 5색 집계(blue/green/yellow/red/gray)
const buildStatusCounts5 = (
  items = [],
  getDate = (x) => x?.date,
  getColor = () => "yellow"
) => {
  const out = {};
  for (const it of items) {
    const raw = getDate(it);
    const ymd =
      typeof raw === "string"
        ? raw.slice(0, 10)
        : raw instanceof Date
          ? format(raw, "yyyy-MM-dd")
          : "";
    if (!ymd) continue;
    const c = getColor(it); // 'blue' | 'green' | 'yellow' | 'red' | 'gray'
    const bucket = (out[ymd] ||= {
      blue: 0,
      green: 0,
      yellow: 0,
      red: 0,
      gray: 0,
      total: 0,
    });
    if (c === "blue") bucket.blue += 1;
    else if (c === "green") bucket.green += 1;
    else if (c === "red") bucket.red += 1;
    else if (c === "gray") bucket.gray += 1;
    else bucket.yellow += 1;
    bucket.total += 1;
  }
  return out;
};

// ─────────────────────────────────────────────
// 컴포넌트
// ─────────────────────────────────────────────
const Checklist = () => {
  const navigate = useNavigate();
  const projectId = localStorage.getItem("projectId");

  // 선택 날짜
  const [selectedDate, setSelectedDate] = useState(new Date());

  // 데이터 / 상태
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // 우측 폼
  const [showForm, setShowForm] = useState(false);
  const [formPayload, setFormPayload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // 상세보기
  const [detailOpen, setDetailOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [detailLoading, setDetailLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [detailError, setDetailError] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [detailDoc, setDetailDoc] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editPayload, setEditPayload] = useState(null);

  // 목록 불러오기 — “저장된 구조” 기준 파싱 (doc_date 통일 + 색상은 status 기반)
  const fetchChecklist = async ({ year, month } = {}) => {
    try {
      setLoading(true);

      const params = { project: projectId };
      if (year && month) {
        params.year = year;
        params.month = month;
      }

      const data = await listSafetyChecklists(params); // ✅ 백엔드도 year/month 지원
      const raw = data?.results || data?.data?.results || data;

      const list = (Array.isArray(raw) ? raw : []).map((row) => {
        const progress = row.progress || "planned";
        const check_date = row.check_date || null;
        const result_date = row.result_date || null;
        const doc_date = row.doc_date || result_date || check_date;
        const unified_label = progress || "미정";
        const color = row.color || progressToColor(progress) || "gray";
        const docDateStr =
          typeof doc_date === "string"
            ? doc_date.slice(0, 10)
            : doc_date
              ? format(new Date(doc_date), "yyyy-MM-dd")
              : null;

        return {
          ...row,
          title: row.title || "(제목 없음)",
          inspector: row.inspector || "",
          confirmer: row.confirmer || "",
          unified_status: unified_label,
          _color: color,
          doc_date: docDateStr,
        };
      });

      setEvents(list);
    } catch (err) {
      console.error("목록 불러오기 실패:", err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 캘린더 집계
  const countsByDate = useMemo(
    () => buildCounts(events, (e) => e?.doc_date),
    [events]
  );

  const statusCountsByDate = useMemo(
    () =>
      buildStatusCounts5(
        events,
        (e) => e?.doc_date,
        (e) => e?._color || "gray"
      ),
    [events]
  );

  // 선택 날짜의 이벤트
  const selectedEvents = useMemo(
    () =>
      events.filter((e) => dayFromStr(e?.doc_date) === fmtDay(selectedDate)),
    [events, selectedDate]
  );

  // 저장(우측 폼)
  const handleSave = async () => {
    try {
      if (!formPayload) return;
      const projectId = localStorage.getItem("projectId");
      if (!projectId) return setError("프로젝트가 선택되지 않았습니다.");

      if (!formPayload.title) return setError("제목을 입력하세요.");
      const meta = formPayload.meta || {};
      if (!meta.check_date) return setError("점검일을 선택하세요.");

      setSaving(true);
      setError("");

      const progress = meta.progress || meta.status || "예정";
      const approvalMode = formPayload.approval === true;
      const saveStatus = approvalMode ? "submit" : "none";

      // eslint-disable-next-line no-unused-vars
      const statusStd = normalizeStatus(progress);

      const res = await saveSafetyDoc({
        project: projectId,
        module: "safety",
        doc_type: formPayload.doc_type,
        title: formPayload.title,
        status: saveStatus,
        meta: {
          ...meta,
          progress,
        },
      });

      const savedDns = res?.dns;
      if (!savedDns) {
        await fetchChecklist();
        setShowForm(false);
        setFormPayload(null);
        alert("저장은 되었지만 문서 ID를 찾지 못했습니다.");
        return;
      }

      await fetchChecklist();
      setShowForm(false);
      setFormPayload(null);
    } catch (e) {
      console.error("저장 실패:", e.response?.data || e.message);
      setError(
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        "저장 중 오류가 발생했습니다."
      );
    } finally {
      setSaving(false);
    }
  };

  // 상세 열기
  const openDetail = (dns, docType) => {
    const project = projectId;
    navigate(`/project/${project}/safety/${docType}/${dns}/view`);
  };
  // const openDetail = async (dns) => {
  //   setDetailOpen(true);
  //   setEditMode(false);
  //   setEditPayload(null);
  //   setDetailLoading(true);
  //   setDetailError("");
  //   setDetailDoc(null);
  //   try {
  //     const tx = await getSafetyDocDetail(dns, { project: projectId });

  //     const meta = tx?.meta || {};
  //     const progress = meta.progress || "미정";
  //     const label = progress.trim() || "미정";

  //     const mergedValues = {
  //       title: tx.title || meta.title || "",
  //       doc_type: tx.doc_type || "plan",
  //       check_type: meta.check_type || "",
  //       check_date: meta.check_date || "",
  //       result_date: meta.result_date || "",
  //       overall_result: meta.overall_result || "",
  //       memo: meta.memo || "",
  //       progress: progress,
  //       items: meta.items || [],
  //       scar_actions: meta.scar_actions || [],
  //       scar_problem: meta.scar_problem || "",
  //       scar_immediate: meta.scar_immediate || "",
  //       scar_root_cause: meta.scar_root_cause || "",
  //       photos_meta: meta.photos_meta || [],
  //     };

  //     setDetailDoc({
  //       id: tx.dns,
  //       dns: tx.dns,
  //       form_type: "ISC",
  //       title: tx.title || mergedValues.title || "(제목 없음)",
  //       updated_at: tx.updated_at || mergedValues.check_date || null,
  //       meta: mergedValues,
  //       unified_status: progress,
  //     });
  //   } catch (e) {
  //     setDetailError(e?.response?.data?.detail || "불러오기 실패");
  //   } finally {
  //     setDetailLoading(false);
  //   }
  // };
  const closeDetail = () => setDetailOpen(false);

  // 수정 저장
  const handleUpdate = async () => {
    if (!detailDoc?.id) return;
    if (!editPayload) {
      alert("변경된 내용이 없습니다.");
      return;
    }
    try {
      setSaving(true);

      const v = editPayload;
      const check_date = v.check_date ?? detailDoc.meta?.check_date ?? null;
      const result_date = v.result_date ?? detailDoc.meta?.result_date ?? null;

      const values = {
        type: "standard",
        project: projectId,
        form_type: (detailDoc.form_type || "ISC").toUpperCase(),
        title: v.title ?? detailDoc.values?.title ?? "",
        memo: v.memo ?? detailDoc.meta?.memo ?? "",
        check_type: v.check_type || v.type || detailDoc.meta?.check_type,
        inspector: v.inspector || detailDoc.values?.inspector || "",
        confirmer: v.confirmer || detailDoc.values?.confirmer || "",
        visibility: v.visibility || detailDoc.values?.visibility || "public",
        referees: v.referees || detailDoc.values?.referees || [],
        items: Array.isArray(v.items)
          ? v.items
          : Array.isArray(detailDoc.meta?.items)
            ? detailDoc.meta.items
            : [],
        check_date,
        result_date,
        status: normalizeStatus(
          v.status || detailDoc.values?.status || "임시저장"
        ),
        inspection_result:
          v.inspection_result ||
          detailDoc.values?.inspection_result ||
          (detailDoc.unified_status === "예정" ? "예정" : ""),
      };

      // 상태 기준 doc_date 재산출
      const statusStd2 = normalizeStatus(values.status ?? "");
      const newDocDate = ["적합(완료)", "부적합", "긴급", "진행 중"].includes(
        statusStd2 || "예정"
      )
        ? values.result_date || values.check_date
        : values.check_date;

      await saveSafetyDoc({
        project: projectId,
        form_type: "ISC",
        dns: detailDoc.dns ?? detailDoc.id,
        body_json: { layout: null, values },
        template_title: values.title,
        status: "draft",
        doc_date: newDocDate || null,
      });

      await openDetail(detailDoc.id, detailDoc.doc_type);
      await fetchChecklist({
        year: new Date(values.check_date).getFullYear(),
        month: new Date(values.check_date).getMonth() + 1,
      });
      setEditMode(false);
      setEditPayload(null);
    } catch (e) {
      alert(
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        "수정 저장 중 오류가 발생했습니다."
      );
    } finally {
      setSaving(false);
    }
  };

  // 통계 계산
  const stats = useMemo(() => {
    const green = events.filter((e) => e._color === "green").length;
    const yellow = events.filter((e) => e._color === "yellow").length;
    const red = events.filter((e) => e._color === "red").length;
    const total = events.length;
    const rate = total ? Math.round((green / total) * 100) : 0;
    return { green, yellow, red, total, rate };
  }, [events]);

  return (
    <div
      className="mobile-page-root mb-16"
      style={{ width: '100%', maxWidth: '100vw', minWidth: 0, display: 'flex', justifyContent: 'center', overflowX: 'hidden', boxSizing: 'border-box' }}
    >
      <div
        className="mobile-page-root-inner space-y-3"
        style={{ width: '100%', maxWidth: '480px', margin: '0 auto', padding: '0 1rem 1rem', boxSizing: 'border-box', overflowX: 'hidden' }}
      >
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-3 border min-w-0">
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-bold text-gray-900 truncate">
                안전 점검 일정
              </h2>
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                프로젝트 <b>{projectId || "-"}</b> 기준 점검 캘린더입니다.
              </p>
            </div>
            <button
              onClick={() => {
                setFormPayload(null);
                setShowForm(true);
              }}
              className="btn-save text-xs px-2.5 py-1.5 flex-shrink-0 whitespace-nowrap"
            >
              일정 등록
            </button>
          </div>

          {/* 양호/조치중/주의 통계 */}
          <div className="bg-gray-50 rounded-lg p-2.5 mb-2.5 border border-gray-200 min-w-0 overflow-hidden">
            <MobileSafetySignalLight
              onLightClick={(status) => console.log("filter:", status)}
            />
          </div>

          {/* 레전드 - 2행으로 배치 */}
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="flex items-center gap-1 flex-wrap min-w-0">
              <StatusPill label="적합" color="green" />
              <StatusPill label="부적합 · 긴급" color="red" />
            </div>
            <div className="flex items-center gap-1 flex-wrap min-w-0">
              <StatusPill label="점검 예정" color="yellow" />
              <StatusPill label="조치 필요" color="yellow" />
              <StatusPill label="진행 중" color="yellow" />
              <StatusPill label="취소" color="gray" />
            </div>
          </div>
        </div>

        {/* 캘린더 */}
        <div className="bg-white rounded-lg border border-gray-200 p-2.5 overflow-hidden min-w-0">
          <div className="overflow-hidden min-w-0 w-full">
            <ReusableCalendar
              value={selectedDate}
              onChange={setSelectedDate}
              countsByDate={countsByDate}
              statusCountsByDate={statusCountsByDate}
              onMonthChange={(date) => {
                const month = date.getMonth() + 1;
                const year = date.getFullYear();
                fetchChecklist({ year, month });
              }}
              className="w-full"
            />
          </div>
        </div>

        {/* 일정 목록 */}
        {!showForm && (
          <div className="min-w-0">
            <div className="bg-white rounded-lg border border-gray-200 p-3 min-w-0">
              <>
                <div className="mb-2.5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1 truncate">
                    {format(selectedDate, "yyyy년 MM월 dd일")} 일정
                  </h3>
                  <p className="text-xs text-gray-500 break-words">
                    목록에서 항목을 선택하면 상세 페이지로 이동할 수 있어요.
                  </p>
                </div>

                {selectedEvents.length > 0 ? (
                  <div className="space-y-2 min-w-0">
                    {selectedEvents.map((e) => (
                      <button
                        key={e.dns}
                        onClick={() => openDetail(e.dns, e.doc_type)}
                        className="w-full text-left rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm active:bg-gray-50 transition-all hover:shadow-md min-w-0"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2 min-w-0">
                          <p className="truncate text-sm font-semibold text-gray-900 flex-1 min-w-0">
                            {e.title}
                          </p>
                          <div className="flex-shrink-0">
                            <StatusPill
                              label={progressLabel(e.progress)}
                              color={progressToColor(e.progress)}
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 truncate mb-2 min-w-0">
                          <span className="font-medium text-gray-600">문서번호:</span> {e.dns}
                        </p>

                        <div className="flex flex-col gap-1 text-xs text-gray-600 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-gray-700 min-w-[45px] flex-shrink-0">점검자:</span>
                            <span className="truncate min-w-0">{e.inspector || "-"}</span>
                          </div>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-gray-700 min-w-[45px] flex-shrink-0">확인자:</span>
                            <span className="truncate min-w-0">{e.confirmer || "-"}</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-xs text-gray-500 min-w-0">
                    <p className="mb-1">선택한 날짜에 일정이 없습니다.</p>
                    <p className="break-words">상단의 <b className="text-gray-700">일정 등록</b> 버튼으로 추가하세요.</p>
                  </div>
                )}
              </>
            </div>
          </div>
        )}

        {/* 일정 등록 바텀 시트 */}
        <MobileSidePanel
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setFormPayload(null);
            setError("");
          }}
          title="점검 일정 등록"
          footer={
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowForm(false);
                  setFormPayload(null);
                  setError("");
                }}
                className="btn-cancel flex-1 text-sm px-4 py-2.5 whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-save flex-1 text-sm px-4 py-2.5 disabled:opacity-60 whitespace-nowrap"
              >
                {saving ? "저장 중..." : "저장"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            {/* 안내 문구 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                점검일과 상태를 입력하면 캘린더에 즉시 반영됩니다.
              </p>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">
                {error}
              </div>
            )}

            {/* 폼 내용 */}
            <div className="min-w-0 overflow-x-hidden">
              <MobileChecklistEditor
                initial={{}}
                readOnly={false}
                onChange={setFormPayload}
              />
            </div>
          </div>
        </MobileSidePanel>
      </div>

      {/* 상세 사이드 패널 (버튼만 컴팩트/색 변경) */}
      <MobileSidePanel
        isOpen={detailOpen}
        onClose={closeDetail}
        title={detailDoc?.title || "문서 상세"}
        headerActions={
          detailDoc?.id ? (
            <div className="flex items-center gap-2 overflow-x-auto">
              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="btn-edit text-xs px-3 py-1.5 flex-shrink-0 whitespace-nowrap"
                >
                  수정
                </button>
              ) : (
                <>
                  <button
                    onClick={handleUpdate}
                    disabled={saving}
                    className="btn-save text-xs px-3 py-1.5 disabled:opacity-60 flex-shrink-0 whitespace-nowrap"
                  >
                    {saving ? "저장 중..." : "저장"}
                  </button>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setFormPayload(null);
                    }}
                    className="btn-cancel text-xs px-3 py-1.5 flex-shrink-0 whitespace-nowrap"
                  >
                    취소
                  </button>
                </>
              )}

              <button
                onClick={async () => {
                  if (!detailDoc?.id) return;
                  if (!window.confirm("이 문서를 삭제하시겠습니까?")) return;
                  try {
                    setDeleting(true);
                    await softDeleteDoc(detailDoc.id, true);
                    alert("문서가 삭제되었습니다.");
                    setDetailOpen(false);
                    await fetchChecklist();
                  } catch (e) {
                    console.error(e);
                    alert(`삭제 실패: ${e?.message || "알 수 없는 오류"}`);
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
                className="btn-delete-sm text-xs px-3 py-1.5 disabled:opacity-60 flex-shrink-0 whitespace-nowrap"
              >
                {deleting ? "삭제 중..." : "삭제"}
              </button>

              <button
                onClick={() =>
                  navigate(
                    `/project/${projectId}/safety/${detailDoc.doc_type}/${detailDoc.id}/view`
                  )
                }
                className="btn-basic text-xs px-3 py-1.5 flex-shrink-0 whitespace-nowrap"
              >
                전체 보기
              </button>
            </div>
          ) : null
        }
      >
        {detailLoading && (
          <div className="text-xs text-gray-500">불러오는 중…</div>
        )}
        {detailError && (
          <div className="text-xs text-rose-600">{detailError}</div>
        )}
        {!detailLoading && !detailError && detailDoc && (
          <div className="space-y-2">
            <div className="rounded-lg border bg-white p-3 text-xs text-gray-600">
              <div>
                유형: <b>{detailDoc.form_type}</b>
              </div>
              <div className="mt-0.5">
                진행상태:{" "}
                <StatusPill
                  label={progressLabel(detailDoc.unified_status)}
                  color={progressToColor(detailDoc.unified_status) || "gray"}
                />
              </div>
              {detailDoc.updated_at && (
                <div className="mt-0.5">
                  수정일: {new Date(detailDoc.updated_at).toLocaleString()}
                </div>
              )}
            </div>

            {!editMode ? (
              <ChecklistView doc={detailDoc} readOnly />
            ) : (
              <MobileChecklistEditor
                initial={detailDoc.meta}
                readOnly={false}
                onChange={setEditPayload}
              />
            )}
          </div>
        )}
      </MobileSidePanel>
    </div>
  );
};

export default Checklist;
